import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { KpiCard } from './KpiCard';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { Lightbulb, Wand2, Rocket, Users } from 'lucide-react';
import type { UsageMetrics } from '@/hooks/useExecutiveUsageMetrics';
import { formatBucketLabel, Granularity } from '@/lib/timeSeries';

const SOLUTION_LABELS: Record<string, string> = {
  '1': 'Contenido con IA',
  '2': 'Vibecoding',
  '3': 'Asistentes/Agentes',
  '4': 'Automatizaciones',
  '5': 'Análisis de Datos',
};

interface Props {
  data?: UsageMetrics;
  isLoading: boolean;
  granularity: Granularity;
}

export function ChallengesTab({ data, isLoading, granularity }: Props) {
  if (isLoading || !data) {
    return <div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-72" /></div>;
  }

  const c = data.challenges;
  const buckets = Array.from(new Set([...c.series.map(s => s.bucket), ...c.solutions_series.map(s => s.bucket)])).sort();
  const series = buckets.map(b => ({
    label: formatBucketLabel(b, granularity),
    retos: c.series.find(s => s.bucket === b)?.retos ?? 0,
    soluciones: c.solutions_series.find(s => s.bucket === b)?.soluciones ?? 0,
  }));

  const funnel = [
    { name: 'Retos creados', value: c.totals.retos },
    { name: 'Con solución', value: c.totals.retos_con_solucion },
    { name: 'Soluciones', value: c.totals.soluciones },
    { name: 'Convertidas a proyecto', value: c.totals.convertidas },
  ];

  const convRate = c.totals.soluciones > 0 ? Math.round((c.totals.convertidas / c.totals.soluciones) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Retos creados" value={c.totals.retos.toLocaleString('es-CO')} icon={<Lightbulb className="w-4 h-4" />} accent="primary" />
        <KpiCard label="Participantes activos" value={c.totals.usuarios.toLocaleString('es-CO')} icon={<Users className="w-4 h-4" />} accent="info" />
        <KpiCard label="Soluciones generadas" value={c.totals.soluciones.toLocaleString('es-CO')} hint={`${c.totals.retos_con_solucion} retos con solución`} icon={<Wand2 className="w-4 h-4" />} accent="accent" />
        <KpiCard label="Convertidas a proyecto" value={c.totals.convertidas.toLocaleString('es-CO')} hint={`${convRate}% de conversión`} icon={<Rocket className="w-4 h-4" />} accent="success" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Evolución del laboratorio</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Legend />
                <Bar dataKey="retos" name="Retos" fill="hsl(var(--primary))" radius={[4,4,0,0]} />
                <Bar dataKey="soluciones" name="Soluciones" fill="hsl(var(--accent))" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Embudo reto → proyecto</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={funnel} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="name" width={160} fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Bar dataKey="value" name="Total" fill="hsl(var(--secondary))" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="glass-card">
        <CardHeader><CardTitle className="text-base">Tipos de solución explorados</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={c.by_type.map(t => ({ ...t, name: SOLUTION_LABELS[t.tipo] ?? t.tipo }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
              <Legend />
              <Bar dataKey="total" name="Soluciones" fill="hsl(var(--primary))" radius={[4,4,0,0]} />
              <Bar dataKey="convertidas" name="Convertidas" fill="hsl(var(--success))" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
